import { useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  fetchProviderIdWithEntra,
  fetchGroupId,
  fetchGroupPlans,
  fetchProviderOrganization,
  fetchUserByEntraId,
  fetchProvidersByGroupId,
  findVirtualProvider,
} from '../helpers/apiHelpers';
import { ACTION_TYPES } from '../constants/formConstants';
import useAuth from './useAuth';

/**
 * Custom hook for fetching plans and provider/group data
 * Handles the chain of API calls needed to load available plans.
 *
 * Supports two login types:
 *  1. Provider user — sub maps directly to a provider via provideridbyentraid
 *  2. User entity  — sub maps to a User record; org context is derived from
 *     the user's organizationGroup. If the user is not tied to a specific org,
 *     the UI must call selectOrganization() before plans can be loaded.
 */
const usePlans = () => {
  const { isAuthenticated, acquireToken } = useAuth();

  // Plan and provider data
  const [plans, setPlans] = useState([]);
  const [planIdMap, setPlanIdMap] = useState([]);
  const [groupIdMap, setGroupIdMap] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [providerId, setProviderId] = useState('');

  // Org selection state — only populated for User entity logins at org-group level
  const [pendingOrgSelection, setPendingOrgSelection] = useState(false);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Cache for the full providers list and group map so selectOrganization
  // can switch orgs without re-fetching from the API.
  const cachedProvidersRef = useRef([]);
  const cachedGroupIdMapRef = useRef(null);

  // Available action types (based on group permissions)
  const [availableActionTypes, setAvailableActionTypes] = useState([
    ACTION_TYPES.CREATE_CASE,
    ACTION_TYPES.DEBUG_INFO,
  ]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Determines if the current group can create cases
   */
  const canCreateCases = useCallback((groupId) => {
    const allowedGroups = process.env.REACT_APP_ALLOWED_GROUPS_TO_CREATE_CASES;
    return allowedGroups === '*' || groupId === allowedGroups;
  }, []);

  /**
   * Shared tail of the fetch flow: loads plans and updates all derived state.
   * Called by both fetchPlans (primary and fallback paths) and selectOrganization.
   */
  const applyOrgAndPlans = useCallback(
    async (orgId, resolvedProviderId, resolvedGroupIdMap, accessToken) => {
      const fetchedPlanIdMap = await fetchGroupPlans(
        resolvedGroupIdMap.id,
        orgId,
        resolvedProviderId,
        accessToken
      );
      setPlanIdMap(fetchedPlanIdMap);
      setPlans(fetchedPlanIdMap.map((plan) => plan.name));
      setProviderId(resolvedProviderId);
      setOrganizationId(orgId);
      setGroupIdMap(resolvedGroupIdMap);

      if (canCreateCases(resolvedGroupIdMap.id)) {
        setAvailableActionTypes([ACTION_TYPES.CREATE_CASE, ACTION_TYPES.DEBUG_INFO]);
      } else {
        setAvailableActionTypes([ACTION_TYPES.DEBUG_INFO]);
      }
    },
    [canCreateCases]
  );

  /**
   * Fetches all plan and provider data.
   *
   * Primary path:   token → sub → providerId → groupId/orgId → plans
   * Fallback path:  token → sub → User entity → organizationGroup
   *                   → if user has specific org: virtual provider → plans
   *                   → if org-group level only:  set pendingOrgSelection, pause
   */
  const fetchPlans = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('Waiting for MSAL authentication...');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const accessToken = await acquireToken();
      const decoded = jwtDecode(accessToken);
      const sub = decoded.sub;

      let resolvedProviderId;
      let fetchedGroupIdMap;
      let resolvedOrgId;

      try {
        // Primary path: sub maps to a provider
        const fetchedProviderId = await fetchProviderIdWithEntra(sub, accessToken);

        const [groupIdMapResult, orgMap] = await Promise.all([
          fetchGroupId(fetchedProviderId, accessToken),
          fetchProviderOrganization(fetchedProviderId, accessToken),
        ]);

        resolvedProviderId = fetchedProviderId;
        fetchedGroupIdMap = groupIdMapResult;
        resolvedOrgId = orgMap.id;
      } catch {
        // Fallback path: sub maps to a User entity (not a provider)
        const userContext = await fetchUserByEntraId(sub, accessToken);
        fetchedGroupIdMap = {
          id: userContext.organizationGroup.id,
          name: userContext.organizationGroup.name,
        };

        const providers = await fetchProvidersByGroupId(fetchedGroupIdMap.id, accessToken);
        cachedProvidersRef.current = providers;
        cachedGroupIdMapRef.current = fetchedGroupIdMap;

        if (userContext.organization) {
          // User is tied to a specific org — resolve virtual provider directly
          resolvedOrgId = userContext.organization.id;
          const vp = findVirtualProvider(providers, resolvedOrgId);
          resolvedProviderId = vp.providerId;
        } else {
          // User is at org-group level — need the user to pick an org first
          const uniqueOrgs = [
            ...new Map(
              providers.map((p) => [
                p.organization.organizationId,
                { organizationId: p.organization.organizationId, name: p.organization.name },
              ])
            ).values(),
          ];
          setAvailableOrgs(uniqueOrgs);
          setGroupIdMap(fetchedGroupIdMap);
          setPendingOrgSelection(true);
          setIsLoading(false);
          return; // Pause — resume via selectOrganization()
        }
      }

      await applyOrgAndPlans(resolvedOrgId, resolvedProviderId, fetchedGroupIdMap, accessToken);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, acquireToken, applyOrgAndPlans]);

  // Fetch plans on mount when authenticated
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  /**
   * Called by the UI when the user selects or changes their organization.
   * Finds the virtual provider for that org from the cached providers list
   * and reloads plans — no extra API calls needed for the provider lookup.
   * @param {{ organizationId: string, name: string }} org
   */
  const selectOrganization = useCallback(
    async (org) => {
      setSelectedOrg(org);
      setPendingOrgSelection(false);
      setIsLoading(true);
      setError(null);

      try {
        const vp = findVirtualProvider(cachedProvidersRef.current, org.organizationId);
        const accessToken = await acquireToken();
        await applyOrgAndPlans(org.organizationId, vp.providerId, cachedGroupIdMapRef.current, accessToken);
      } catch (err) {
        console.error('Error selecting organization:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [acquireToken, applyOrgAndPlans]
  );

  /**
   * Find plan details by plan name
   */
  const getPlanByName = useCallback(
    (planName) => planIdMap.find((plan) => plan.name === planName) || null,
    [planIdMap]
  );

  const hasCreateCasePermission = availableActionTypes.includes(ACTION_TYPES.CREATE_CASE);

  return {
    // Data
    plans,
    planIdMap,
    groupIdMap,
    organizationId,
    providerId,
    availableActionTypes,

    // Org selection (User entity type only)
    pendingOrgSelection,
    availableOrgs,
    selectedOrg,
    selectOrganization,

    // State
    isLoading,
    error,

    // Helpers
    getPlanByName,
    hasCreateCasePermission,

    // Actions
    refetchPlans: fetchPlans,
  };
};

export default usePlans;
